import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import EquilibriumLayout from "@/components/equilibrium/EquilibriumLayout";
import { fetchEqModules, submitEqDiagnostic } from "@/lib/eq";

const SIGNALS = [
  "Dwell accumulation",
  "Empty miles",
  "Trailer imbalance",
  "Detention exposure",
  "Forecast volatility",
  "EDI / API gaps",
  "Premium freight burn",
  "Cost-to-serve drift",
  "Vendor scheduling friction",
  "Reconciliation lag",
];

export default function DiagnosticPage() {
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    problem_statement: "",
    signals_observed: [],
    target_module: "spyglass",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchEqModules().then(setModules).catch(() => setModules([]));
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSignal = (s) =>
    setForm((f) => ({
      ...f,
      signals_observed: f.signals_observed.includes(s)
        ? f.signals_observed.filter((x) => x !== s)
        : [...f.signals_observed, s],
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company || form.problem_statement.length < 10) {
      toast.error("Name, email, company, and a problem statement (10+ chars) are required.");
      return;
    }
    setLoading(true);
    try {
      const data = await submitEqDiagnostic(form);
      setResult(data);
      toast.success("Diagnostic received. Confidence + entropy computed.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <EquilibriumLayout>
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 py-12">
        <button
          onClick={() => navigate("/equilibrium")}
          className="flex items-center gap-2 text-sm text-axiom-muted hover:text-axiom-text mb-8"
          data-testid="eq-diag-back"
        >
          <ArrowLeft size={14} /> Platform
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="num-label">OPERATOR DIAGNOSTIC</div>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl text-axiom-text font-bold tracking-tight leading-tight">
              Submit a structured <br />
              <span className="text-axiom-accent">operational signal.</span>
            </h1>
            <p className="mt-5 text-axiom-muted text-base leading-relaxed max-w-md">
              Provide what you're seeing in the network. Equilibrium-AI will compute confidence,
              estimate entropy, and recommend which modules are the right entry point.
            </p>

            <div className="mt-10 border-l border-white/10 pl-5 space-y-5">
              <div>
                <div className="num-label">CONFIDENTIAL</div>
                <p className="text-sm text-axiom-text mt-1">
                  Submission stays inside Equilibrium-AI's provenance ledger. Forwardable only by
                  authenticated operators.
                </p>
              </div>
              <div>
                <div className="num-label">FORMAT</div>
                <p className="text-sm text-axiom-text mt-1">
                  Structured intake — name, company, observed signals, problem statement.
                </p>
              </div>
              <div>
                <div className="num-label">OUTCOME</div>
                <p className="text-sm text-axiom-text mt-1">
                  A confidence score, entropy estimate, and module recommendation.
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {result ? (
              <ResultPanel result={result} onReset={() => { setResult(null); setForm({ name:"", email:"", company:"", role:"", problem_statement:"", signals_observed:[], target_module:"spyglass" }); }} navigate={navigate} />
            ) : (
              <form onSubmit={handleSubmit} className="axiom-card p-6 md:p-8" data-testid="eq-diag-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Name *">
                    <input
                      data-testid="eq-diag-name"
                      type="text"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      className={inputCls}
                      placeholder="Jane Operator"
                    />
                  </Field>
                  <Field label="Email *">
                    <input
                      data-testid="eq-diag-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      className={inputCls}
                      placeholder="jane@company.com"
                    />
                  </Field>
                  <Field label="Company *">
                    <input
                      data-testid="eq-diag-company"
                      type="text"
                      value={form.company}
                      onChange={(e) => update("company", e.target.value)}
                      className={inputCls}
                      placeholder="Acme Logistics"
                    />
                  </Field>
                  <Field label="Role">
                    <input
                      data-testid="eq-diag-role"
                      type="text"
                      value={form.role}
                      onChange={(e) => update("role", e.target.value)}
                      className={inputCls}
                      placeholder="VP Supply Chain"
                    />
                  </Field>
                </div>

                <div className="mt-5">
                  <div className="num-label mb-2">OBSERVED SIGNALS</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {SIGNALS.map((s) => (
                      <button
                        type="button"
                        key={s}
                        data-testid={`eq-diag-signal-${s.replace(/\W/g, '-').toLowerCase()}`}
                        onClick={() => toggleSignal(s)}
                        className={`text-left text-xs px-3 py-2 border transition-all ${
                          form.signals_observed.includes(s)
                            ? "border-axiom-accent bg-axiom-accent/5 text-axiom-text"
                            : "border-white/10 text-axiom-muted hover:border-white/25"
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <span
                            className={`signal-dot ${
                              form.signals_observed.includes(s) ? "animate-pulse-signal" : "opacity-30"
                            }`}
                            style={{ width: 5, height: 5 }}
                          />
                          {s}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-5">
                  <div className="num-label mb-2">TARGET MODULE</div>
                  <div className="flex flex-wrap gap-2">
                    {modules.map((m) => (
                      <button
                        type="button"
                        key={m.key}
                        data-testid={`eq-diag-target-${m.key}`}
                        onClick={() => update("target_module", m.key)}
                        className={`text-xs px-3 py-1.5 border font-mono uppercase tracking-wider transition-colors ${
                          form.target_module === m.key
                            ? "border-axiom-accent text-axiom-accent"
                            : "border-white/10 text-axiom-muted hover:border-white/25"
                        }`}
                      >
                        {m.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-5">
                  <Field label="Problem statement *">
                    <textarea
                      data-testid="eq-diag-problem"
                      rows={6}
                      value={form.problem_statement}
                      onChange={(e) => update("problem_statement", e.target.value)}
                      className={`${inputCls} resize-none`}
                      placeholder="What is the network telling you? Be specific — dwell at a DC, EDI gaps, forecast bias on a SKU group, detention exposure on a lane..."
                    />
                  </Field>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="eq-diag-submit"
                  className="btn-axiom mt-6 w-full justify-center disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Scoring signal...
                    </>
                  ) : (
                    <>
                      Submit Diagnostic <ArrowRight size={16} />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </EquilibriumLayout>
  );
}

function ResultPanel({ result, onReset, navigate }) {
  return (
    <div className="axiom-card p-6 md:p-8" data-testid="eq-diag-result">
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck size={16} className="text-axiom-accent" />
        <span className="num-label">DIAGNOSTIC.RECEIVED</span>
      </div>
      <h2 className="font-display text-3xl text-axiom-text font-bold tracking-tight">
        Signal scored.
      </h2>
      <p className="mt-2 text-axiom-muted text-sm leading-relaxed">
        Your submission has been written to the Equilibrium-AI provenance ledger and is now
        traceable by operator-grade authentication.
      </p>

      <div className="mt-6 grid grid-cols-2 gap-px bg-white/10 border border-white/10">
        <div className="bg-axiom-bg p-5">
          <div className="num-label">CONFIDENCE SCORE</div>
          <div className="mt-2 font-display text-4xl text-axiom-accent">
            {(result.confidence_score * 100).toFixed(1)}%
          </div>
          <div className="num-label mt-2">Higher = clearer operational signal.</div>
        </div>
        <div className="bg-axiom-bg p-5">
          <div className="num-label">ENTROPY ESTIMATE</div>
          <div className="mt-2 font-display text-4xl text-axiom-text">
            {result.entropy_estimate.toFixed(2)}
          </div>
          <div className="num-label mt-2">Lower = network closer to coherent truth.</div>
        </div>
      </div>

      <div className="mt-6">
        <div className="num-label mb-3">SUGGESTED MODULES</div>
        <div className="flex flex-wrap gap-2">
          {result.suggested_modules.map((k) => (
            <button
              key={k}
              data-testid={`eq-diag-suggested-${k}`}
              onClick={() => navigate(`/equilibrium/modules/${k}`)}
              className="text-xs font-mono uppercase tracking-wider border border-axiom-accent/40 text-axiom-accent px-3 py-1.5 hover:bg-axiom-accent/5"
            >
              {k}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 border-t border-white/10 pt-6">
        <div className="num-label mb-2">PROVENANCE</div>
        <div className="text-xs font-mono text-axiom-muted leading-relaxed">
          <div>RECORD.ID · {result.id}</div>
          <div>TIMESTAMP · {result.created_at}</div>
          <div>TARGET.MODULE · {result.target_module}</div>
        </div>
      </div>

      <div className="mt-6 flex gap-3">
        <button onClick={onReset} className="btn-axiom-outline text-xs flex-1 justify-center">
          Submit another
        </button>
        <button
          onClick={() => navigate("/equilibrium/console")}
          className="btn-axiom text-xs flex-1 justify-center"
        >
          Open Console <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}

const inputCls =
  "w-full bg-axiom-bg border border-white/10 px-4 py-3 text-sm text-axiom-text placeholder:text-axiom-dim focus:border-axiom-accent focus:outline-none";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="num-label">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
